/** @module components/subscription/upgrade */
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useLoc, useActions } from "app/utils/hooks";
import { RootState } from "app/reducers";
import { fetchPlans } from "app/actions/subscription-plan";
import { cls } from "app/utils";
import { SubscriptionPlanModel } from "app/models";
import CheckBox from "app/components/inputs/CheckBox";
import { Loc } from "app/components/helpers";
import styles from "./styles.module.scss";
import i18n from "app/i18n";

type PlanTypeDef = {
  id: string;
  title: string;
  subtitle: string;
};

const ENTERPRISE_PLAN: SubscriptionPlanModel = {
  id: "coach+enterprise",
  amount: 0,
  currency: "usd",
  termKind: "Enterprise",
  productKind: "CoachPlus",
};

export const PLAN_TYPES: PlanTypeDef[] = [
  {
    id: "coachpro+yearly",
    title: i18n.t("CoachNow PRO Yearly"),
    subtitle: i18n.t("{{cost}} / year"),
  },
  {
    id: "coachpro+monthly",
    title: i18n.t("CoachNow PRO Monthly"),
    subtitle: i18n.t("{{cost}} / month"),
  },
  {
    id: "coach+yearly",
    title: i18n.t("CoachNow+ Yearly"),
    subtitle: i18n.t("{{cost}} / year"),
  },
  {
    id: "coach+monthly",
    title: i18n.t("CoachNow+ Monthly"),
    subtitle: i18n.t("{{cost}} / month"),
  },
  {
    id: "analyze+yearly",
    title: i18n.t("CoachNow Analyze Yearly"),
    subtitle: i18n.t("{{cost}} / year"),
  },
  {
    id: "analyze+monthly",
    title: i18n.t("CoachNow Analyze Monthly"),
    subtitle: i18n.t("{{cost}} / month"),
  },
  {
    id: "coach+enterprise",
    title: i18n.t("CoachNow Academy"),
    subtitle: i18n.t("Contact us for pricing"),
  },
];

type PropsDef = {
  selectPlan: string;
  // The plan ids to offer; every plan when absent
  onlyPlans?: string[];
  // The currency the prices are listed in; USD when absent
  currency?: string;
  disabled?: boolean;
  onChange?: (value: SubscriptionPlanModel) => void;
};

const defaultPlan = "coach+yearly";

/**
 * @class PlanTypes
 *
 */
export default function PlanTypes(props: PropsDef) {
  const actions = useActions({ fetchPlans });
  const subscriptionPlan = useSelector(
    (state: RootState) => state.subscriptionPlan,
    shallowEqual,
  );
  const [selected, setSelected] = useState<SubscriptionPlanModel>(null);
  const currency = props.currency || "usd";
  // Prices left in the store by another page or an earlier request may be in
  // another currency: read them only once they are the ones asked for here
  const plans =
    !subscriptionPlan.pending && subscriptionPlan.currency === currency
      ? subscriptionPlan.data
      : null;

  useEffect(() => (actions.fetchPlans(currency), undefined), [currency]);

  const getPlan = (planId: string): SubscriptionPlanModel | null => {
    if (planId === "coach+enterprise") {
      return ENTERPRISE_PLAN;
    } else if (plans != null) {
      return plans.find((m: SubscriptionPlanModel) => m.id === planId) || null;
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (props.onChange) {
      props.onChange(selected);
    }
  }, [selected]);

  useEffect(() => {
    const planId = props.selectPlan || defaultPlan;
    if (plans != null) {
      setSelected(getPlan(planId));
    }
  }, [plans, props.selectPlan]);

  if (selected == null) {
    return <div />;
  }

  const children = PLAN_TYPES.map((plan: PlanTypeDef) => {
    const planModel = getPlan(plan.id);

    if (props.onlyPlans && !props.onlyPlans.includes(plan.id)) {
      return <div key={plan.id} />;
    }

    // Plan isn't in the fetched data (e.g. its price isn't configured in this
    // Stripe environment) — skip it instead of crashing on undefined.amount.
    if (planModel == null) {
      return <div key={plan.id} />;
    }

    return (
      <CheckboxContainer
        key={plan.id}
        type={plan}
        model={planModel}
        selected={selected}
        onChange={() => setSelected(planModel)}
        disabled={props.disabled}
      />
    );
  });

  return <div className={styles.planTypes}>{children}</div>;
}

type CheckProps = {
  key: string;
  type: PlanTypeDef;
  model: SubscriptionPlanModel;
  selected: SubscriptionPlanModel;
  onChange: () => void;
  disabled: boolean;
};

const CheckboxContainer = (props: CheckProps) => {
  const { formatNumber } = useLoc();
  const name = "plan_type";

  // convert cost to a monthly cost
  let costNum: number = props.model.amount;
  // if (props.type.id === 'coach+yearly' || props.type.id === 'coachpro+yearly') {
  //   costNum = Math.floor(costNum / 12);
  // }

  // convert cost to dollars
  costNum = costNum / 100;

  const cost = formatNumber(costNum, {
    style: "currency",
    currency: props.model.currency.toUpperCase(),
  });
  const title = props.type.title;
  const subtitle = props.type.subtitle.replace("{{cost}}", cost);
  const label = (
    <div className={styles.label}>
      <h3>
        <Loc value={title} />
      </h3>
      <h4>
        <Loc value={subtitle} />
      </h4>
    </div>
  );

  const isSelected = props.selected.id === props.type.id;

  return (
    <div className={cls(styles.radio, isSelected ? styles.selected : "")}>
      <CheckBox
        name={name}
        label={label}
        checked={isSelected}
        onChange={props.onChange}
        disabled={props.disabled}
      />
      <div className={styles.clearFloat}></div>
    </div>
  );
};
